import { useContext, useEffect, useMemo, useState } from 'react';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { getIsNodeFile, getShareableChildIds, sortDataRoomNodes } from 'utils';
import { DataRoomSelectableNode } from '../../../../../components/FileCheckSelector/types';
import dataRoomApi from '../../../../../dataRoomApi';
import { DataRoomNode } from '../../../../../models/DataRoomNode';
import DataRoomContext from '../../../../../providers/DataRoom/DataRoom.context';

// A link records whole folders and individual files separately, and the shared
// helper only reads the file list — a folder shared outright, whose files are not
// enumerated, would read as unshared. Both lists are consulted here.
//
// Every node is marked disabled: this tree reports a selection, it does not
// collect one.
const prepareSharedNodes = (
  nodes: DataRoomNode[],
  fileIds: string[] = [],
  folderIds: string[] = [],
): DataRoomSelectableNode[] =>
  nodes.map((node) => {
    if (getIsNodeFile(node))
      return {
        ...node,
        checked: fileIds.includes(node._id),
        isMixed: false,
        disabled: true,
      };

    const shareableIds = getShareableChildIds(node) ?? [];

    const isWholeFolderShared = folderIds.includes(node._id);
    const sharedChildren = shareableIds.filter((id) => fileIds.includes(id));

    return {
      ...node,
      checked: isWholeFolderShared || !!sharedChildren.length,
      // A folder shared outright is fully shared, however few of its files the
      // link happens to list.
      isMixed:
        !isWholeFolderShared &&
        !!sharedChildren.length &&
        sharedChildren.length !== shareableIds.length,
      disabled: true,
    };
  });

// Seeds the file selector with the room's tree, ticked to match what the link
// already shares.
export default function useSecureLinkSelectorNodes(
  link: DataRoomShareableLink,
  enabled: boolean,
) {
  const { _id: linkId, fileIds, folderIds } = link ?? {};

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { popServerError } = useContext(PopNotificationsContext);

  const [nodes, setNodes] = useState<DataRoomSelectableNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // What the rows a folder loads on opening are matched against.
  const initiallyCheckedNodes = useMemo(
    () => [...(fileIds ?? []), ...(folderIds ?? [])],
    [fileIds, folderIds],
  );

  // Only the root level is fetched up front; the folder rows load their own
  // children as they are opened.
  useEffect(() => {
    if (!enabled || !dataRoomId || !linkId) return;

    (async () => {
      try {
        setIsLoading(true);

        const { data } = await dataRoomApi.nodes.getChildren(dataRoomId);

        setNodes(
          prepareSharedNodes(sortDataRoomNodes(data), fileIds, folderIds),
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [enabled, dataRoomId, linkId, fileIds, folderIds, popServerError]);

  return { nodes, setNodes, initiallyCheckedNodes, isLoading };
}
